import React, { createContext, useEffect, useState } from 'react'
import { IUser } from '@types'
import axios from 'axios'
import { compareObject } from '@utils'
import {
  clearToken,
  decodeToken,
  getUsableToken,
  setToken,
  TokenPayload,
} from '../utils/token'

// Kept shape-compatible with the old Cognito context: `session` is now the
// decoded app-JWT payload (truthy while logged in), `setUserSession` takes
// the raw JWT (or null to log out).
export interface AuthObject {
  session: TokenPayload | null
  authenticated: boolean
  roles: string[]
  setUserSession: Function
  user: IUser | null
  getUser: Function
  removeUser: Function
}

const AuthContext = createContext<AuthObject>({
  session: null,
  authenticated: false,
  roles: [],
  setUserSession: () => {},
  user: null,
  getUser: () => {},
  removeUser: () => {},
})
const { Provider } = AuthContext

const AuthProvider = ({ children }: { children: any }) => {
  const [session, setSession] = useState<null | TokenPayload>(null)
  const [user, setUser] = useState<null | IUser>(null)

  useEffect(() => {
    // Rehydrate from localStorage on mount (client only)
    const token = getUsableToken()
    if (token) {
      setSession(decodeToken(token))
    }
  }, [])

  const getUser = () => {
    axios.get(process.env.NEXT_PUBLIC_API_URL + '/me').then(res => {
      if (!compareObject(res.data, user)) {
        setUser(res?.data)
      }
    })
  }

  useEffect(() => {
    if (session && !user) {
      getUser()
    }
  }, [session])

  const setUserSession = (token: string | null) => {
    if (token) {
      setToken(token)
      setSession(decodeToken(token))
    } else {
      clearToken()
      setSession(null)
    }
  }

  return (
    <Provider
      value={{
        session,
        setUserSession,
        authenticated: session !== null,
        roles: session?.roles ?? [],
        user,
        getUser: getUser,
        removeUser: () => {
          setUser(null)
        },
      }}>
      {children}
    </Provider>
  )
}

export { AuthContext, AuthProvider }
